/**
 * Schema definitions for the generic AdminCollection page.
 * Each schema = { title, endpoint, icon (lucide name), fields: [...], itemTitle: (row) => string }.
 *
 * Field types:
 *   - text, textarea, richtext, url, email, image, number, select, checkbox, list (string[]), rating
 */
export const COLLECTION_SCHEMAS = {
  testimonials: {
    title: "Testimonials",
    description: "Client reviews shown across the website.",
    endpoint: "testimonials",
    itemTitle: (r) => r.client_name,
    itemSubtitle: (r) => [r.role, r.company].filter(Boolean).join(" · "),
    fields: [
      { key: "client_name", label: "Client name", type: "text", required: true },
      { key: "company", label: "Company", type: "text" },
      { key: "role", label: "Role / Title", type: "text" },
      { key: "quote", label: "Quote", type: "textarea", required: true, rows: 5 },
      { key: "rating", label: "Rating", type: "rating" },
      { key: "photo_url", label: "Photo", type: "image" },
      { key: "is_visible", label: "Visible on website", type: "checkbox", default: true },
    ],
  },
  faqs: {
    title: "FAQ",
    description: "Frequently asked questions shown in FAQ sections.",
    endpoint: "faqs",
    itemTitle: (r) => r.question,
    itemSubtitle: (r) => r.category || "general",
    fields: [
      { key: "question", label: "Question", type: "text", required: true },
      { key: "answer", label: "Answer", type: "textarea", required: true, rows: 6 },
      { key: "category", label: "Category", type: "select", options: ["general", "services", "pricing", "compliance", "careers", "billing"], default: "general" },
      { key: "is_visible", label: "Visible on website", type: "checkbox", default: true },
    ],
  },
  team: {
    title: "Team Members",
    description: "Team / leadership profiles shown in Team Gallery sections.",
    endpoint: "team",
    itemTitle: (r) => r.name,
    itemSubtitle: (r) => r.role,
    fields: [
      { key: "name", label: "Full name", type: "text", required: true },
      { key: "role", label: "Role / Title", type: "text" },
      { key: "photo_url", label: "Photo", type: "image" },
      { key: "bio", label: "Bio", type: "textarea", rows: 4 },
      { key: "email", label: "Email", type: "email" },
      { key: "linkedin_url", label: "LinkedIn URL", type: "url" },
      { key: "is_visible", label: "Visible on website", type: "checkbox", default: true },
    ],
  },
  "case-studies": {
    title: "Case Studies",
    description: "Project case studies and showcased work.",
    endpoint: "case-studies",
    itemTitle: (r) => r.title,
    itemSubtitle: (r) => [r.client, r.sector].filter(Boolean).join(" · "),
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "slug", label: "Slug", type: "text", required: true, hint: "URL-friendly identifier, e.g. tech-campus-dublin" },
      { key: "client", label: "Client", type: "text" },
      { key: "sector", label: "Sector", type: "text" },
      { key: "summary", label: "Summary", type: "textarea", rows: 3 },
      { key: "cover_image_url", label: "Cover image", type: "image" },
      { key: "body", label: "Body (HTML)", type: "textarea", rows: 8, hint: "Plain HTML accepted." },
      { key: "results", label: "Results / KPIs", type: "list", placeholder: "e.g. 40% faster turnaround" },
      { key: "tags", label: "Tags", type: "list", placeholder: "e.g. industrial" },
      { key: "is_visible", label: "Visible on website", type: "checkbox", default: true },
    ],
  },
  kpis: {
    title: "KPI / Stats",
    description: "Numbers shown in the KPI strip and trust bars (e.g. 12+ years).",
    endpoint: "kpis",
    itemTitle: (r) => `${r.value}${r.suffix ? " " + r.suffix : ""} — ${r.label}`,
    itemSubtitle: (r) => r.icon || "",
    fields: [
      { key: "value", label: "Value", type: "text", required: true, hint: "e.g. 12+, 500, €12.5M" },
      { key: "suffix", label: "Suffix", type: "text", hint: "e.g. years, projects" },
      { key: "label", label: "Label", type: "text", required: true, hint: "e.g. Years in operation" },
      { key: "icon", label: "Icon name (lucide)", type: "text", hint: "e.g. ShieldCheck, Users" },
      { key: "is_visible", label: "Visible on website", type: "checkbox", default: true },
    ],
  },
  sectors: {
    title: "Sectors / Industries",
    description: "Industries you serve (Healthcare, Pharma, Industrial …).",
    endpoint: "sectors",
    itemTitle: (r) => r.name,
    itemSubtitle: (r) => r.summary,
    fields: [
      { key: "name", label: "Sector name", type: "text", required: true },
      { key: "slug", label: "Slug", type: "text", required: true },
      { key: "icon", label: "Icon name (lucide)", type: "text" },
      { key: "summary", label: "Short summary", type: "textarea", rows: 3 },
      { key: "cover_image_url", label: "Cover image", type: "image" },
      { key: "is_visible", label: "Visible on website", type: "checkbox", default: true },
    ],
  },
  banners: {
    title: "Banners / Announcements",
    description: "Global site banner shown above the navigation. Only one active banner is shown at a time.",
    endpoint: "banners",
    itemTitle: (r) => r.message,
    itemSubtitle: (r) => r.is_active ? "ACTIVE" : "Inactive",
    fields: [
      { key: "message", label: "Message", type: "text", required: true, hint: "Short announcement shown to all visitors." },
      { key: "link_url", label: "Link URL (optional)", type: "url" },
      { key: "link_label", label: "Link label", type: "text", hint: "e.g. Learn more" },
      { key: "variant", label: "Style", type: "select", options: ["info", "success", "warning"], default: "info" },
      { key: "is_active", label: "Active (show on site)", type: "checkbox", default: false },
      { key: "starts_at", label: "Starts at (ISO datetime, optional)", type: "text", hint: "e.g. 2026-02-01T09:00:00Z" },
      { key: "ends_at", label: "Ends at (ISO datetime, optional)", type: "text" },
    ],
  },
  subscribers: {
    title: "Newsletter Subscribers",
    description: "Email list. Add subscribers manually or export the list.",
    endpoint: "subscribers",
    itemTitle: (r) => r.email,
    itemSubtitle: (r) => [r.full_name, r.source].filter(Boolean).join(" · "),
    fields: [
      { key: "email", label: "Email", type: "email", required: true },
      { key: "full_name", label: "Full name", type: "text" },
      { key: "source", label: "Source", type: "select", options: ["admin", "website", "import", "campaign"], default: "admin" },
      { key: "tags", label: "Tags", type: "list", placeholder: "e.g. prospect" },
      { key: "is_visible", label: "Active subscriber", type: "checkbox", default: true },
    ],
  },
  redirects: {
    title: "URL Redirects",
    description: "301/302 redirects. Useful for renamed pages or marketing campaigns.",
    endpoint: "redirects",
    itemTitle: (r) => `${r.from_path} → ${r.to_url}`,
    itemSubtitle: (r) => `${r.code} · ${r.note || ""}`,
    fields: [
      { key: "from_path", label: "From path", type: "text", required: true, hint: "e.g. /old-services" },
      { key: "to_url", label: "To URL", type: "text", required: true, hint: "e.g. /services" },
      { key: "code", label: "Status code", type: "select", options: ["301", "302", "307", "308"], default: "301" },
      { key: "note", label: "Internal note", type: "text" },
      { key: "is_visible", label: "Active", type: "checkbox", default: true },
    ],
  },
  "pricing-plans": {
    title: "Pricing Plans",
    description: "Service tiers shown on pricing tables.",
    endpoint: "pricing-plans",
    itemTitle: (r) => r.name,
    itemSubtitle: (r) => r.price,
    fields: [
      { key: "name", label: "Plan name", type: "text", required: true, hint: "e.g. Essential, Standard, Premium" },
      { key: "price", label: "Price", type: "text", required: true, hint: 'e.g. "€450 / month" or "Custom"' },
      { key: "summary", label: "Short tagline", type: "text" },
      { key: "features", label: "Features", type: "list", placeholder: "e.g. Weekly deep clean" },
      { key: "cta_label", label: "Button label", type: "text", default: "Request quote" },
      { key: "cta_url", label: "Button URL", type: "text", default: "/quote" },
      { key: "is_featured", label: "Featured (highlighted)", type: "checkbox" },
      { key: "is_visible", label: "Visible on website", type: "checkbox", default: true },
    ],
  },
  partners: {
    title: "Partners & Clients",
    description: "Client logos and accreditation marks shown on trust strips and About pages.",
    endpoint: "partners",
    itemTitle: (r) => r.name,
    itemSubtitle: (r) => [r.category, r.summary].filter(Boolean).join(" · "),
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "summary", label: "Subtitle / Description", type: "text", hint: "e.g. Quality Mgmt, Approved, Eco-Certified" },
      { key: "logo_url", label: "Logo", type: "image" },
      { key: "website_url", label: "Website URL", type: "url" },
      { key: "category", label: "Category", type: "select", options: ["client", "partner", "accreditation"], default: "client" },
      { key: "is_visible", label: "Visible on website", type: "checkbox", default: true },
    ],
  },
  locations: {
    title: "Office Locations",
    description: "Physical offices and service hubs across Ireland.",
    endpoint: "locations",
    itemTitle: (r) => r.city,
    itemSubtitle: (r) => r.address,
    fields: [
      { key: "city", label: "City", type: "text", required: true },
      { key: "region", label: "Region / County", type: "text" },
      { key: "address", label: "Full address", type: "textarea", rows: 3 },
      { key: "phone", label: "Phone", type: "text" },
      { key: "email", label: "Email", type: "email" },
      { key: "map_url", label: "Google Maps embed URL", type: "url" },
      { key: "hours", label: "Opening hours", type: "text", hint: "e.g. Mon–Fri 08:00–18:00" },
      { key: "is_headquarters", label: "Headquarters", type: "checkbox" },
      { key: "is_visible", label: "Visible on website", type: "checkbox", default: true },
    ],
  },
  documents: {
    title: "Documents & Downloads",
    description: "PDFs and downloadable files (compliance docs, brochures, datasheets).",
    endpoint: "documents",
    itemTitle: (r) => r.title,
    itemSubtitle: (r) => `${r.category} · ${r.file_size || ""}`,
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea", rows: 2 },
      { key: "file_url", label: "File URL", type: "text", required: true, hint: "Upload via Media library and paste the URL here." },
      { key: "category", label: "Category", type: "select", options: ["general", "compliance", "brochure", "datasheet", "case-study"], default: "general" },
      { key: "file_size", label: "File size (display)", type: "text", hint: "e.g. 1.2 MB" },
      { key: "is_visible", label: "Visible on website", type: "checkbox", default: true },
    ],
  },
  "trust-badges": {
    title: "Trust Badges",
    description: "Documentation & accreditation strip shown on the homepage. Add, edit or reorder badges (e.g. Public Liability Insurance, RAMS, SDS, HACCP, SafeContractor, CHAS…).",
    endpoint: "trust-badges",
    itemTitle: (r) => r.label,
    itemSubtitle: (r) => [r.kind, r.sub].filter(Boolean).join(" · "),
    fields: [
      { key: "label", label: "Label", type: "text", required: true, hint: "e.g. Public Liability Insurance, RAMS, HACCP Training" },
      { key: "sub", label: "Sub-caption", type: "text", hint: "Small line under the label. e.g. Insured, Risk + Method, Food Projects" },
      { key: "icon", label: "Icon name (lucide)", type: "text", default: "FileText", hint: "e.g. ShieldCheck, FileText, BadgeCheck, HeartPulse, Utensils, Droplets, Leaf, ScrollText, BookOpen, ClipboardCheck" },
      { key: "kind", label: "Type", type: "select", options: ["doc", "risk", "acc"], default: "doc", hint: "doc = navy, risk = amber, acc (accreditation) = blue" },
      { key: "is_visible", label: "Visible on website", type: "checkbox", default: true },
    ],
  },
};
